import { MessageSquarePlus } from "lucide-react";
import { useState } from "react";

import { captureScreenshot } from "@/components/feedback/capture";
import { FeedbackDialog } from "@/components/feedback/FeedbackDialog";
import { Button } from "@/components/ui/button";

/**
 * The Feedback button: in `PageContainer`'s header on every screen (#319) —
 * every module gets it for free, the same way every module gets the filter bar
 * and the data table — and again in the header of every drawer and sheet.
 *
 * The second place is not a convenience. A drawer is a modal: it covers the
 * page header, and everything behind it is inert, so the button there cannot be
 * reached while a record is open — and the record is exactly where a report is
 * most likely to start (#498). Rendered inside the drawer it is part of the
 * drawer, with its focus trap and its Escape handling, and the dialog it opens
 * stacks above it. Its context names the record by itself: the open record is
 * in the URL, which `useFeedbackContext` reads.
 */
export function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [screenshot, setScreenshot] = useState<Blob | null>(null);
  // Bumped on every click and used as the dialog's `key`, so each report
  // starts from a clean form — a remount rather than an effect resetting
  // state in response to a new screenshot.
  const [session, setSession] = useState(0);

  async function handleClick() {
    setCapturing(true);
    try {
      // Captured before the dialog opens, so the screenshot is the screen
      // being reported on rather than a picture of the feedback dialog
      // itself.
      setScreenshot(await captureScreenshot());
    } finally {
      setCapturing(false);
      setSession((n) => n + 1);
      setOpen(true);
    }
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        // In a drawer's header, where a finger is the input, it is at least
        // 44px square: at phone width it was 38x32 (#523). The negative margin
        // takes back what that adds to the header's height — the hit area
        // reaches into the header's padding rather than making the bar taller.
        className="in-data-[slot=sheet-content]:pointer-coarse:-my-1.5 in-data-[slot=sheet-content]:pointer-coarse:h-11 in-data-[slot=sheet-content]:pointer-coarse:min-w-11"
        disabled={capturing}
        // The visible label goes away below `sm`, and a button with only an
        // icon in it has no name for a screen reader to read. Contains the
        // visible word, so a name that matches it still finds this.
        aria-label="Send feedback"
        onClick={() => {
          void handleClick();
        }}
      >
        <MessageSquarePlus />
        {/* Icon-only on mobile — the label is the thing that gives way when
            the header runs out of room. */}
        <span className="hidden sm:inline">
          {capturing ? "Capturing…" : "Feedback"}
        </span>
      </Button>
      <FeedbackDialog
        key={session}
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setScreenshot(null);
        }}
        screenshot={screenshot}
      />
    </>
  );
}
